// utils.js

export const formatDate = (ts, locale = 'en') => {
  if(!ts) return
  const date = new Date(ts);
  const days = {
      en: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      de: ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag']
  };

  const dayName = days[locale][date.getDay()];
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  let hh = date.getHours();
  const min = String(date.getMinutes()).padStart(2, '0');

  if (locale === 'de') {
      return `${dayName}, ${dd}.${mm}.${yyyy} ${hh}:${min} Uhr`;
  } else {
      const period = hh >= 12 ? 'PM' : 'AM';
      hh = hh % 12 || 12; // Convert 24-hour time to 12-hour format, treating 0 as 12
      return `${dayName}, ${mm}/${dd}/${yyyy} ${hh}:${min} ${period}`;
  }
};

export const formatDateWithoutDay = (ts, locale = 'en') => {
  if(!ts) return
  const date = new Date(ts);

  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  let hh = date.getHours();
  const min = String(date.getMinutes()).padStart(2, '0');

  if (locale === 'de') {
      return `${dd}.${mm}.${yyyy} ${hh}:${min} Uhr`;
  } else {
      const period = hh >= 12 ? 'PM' : 'AM';
      hh = hh % 12 || 12; // Convert 24-hour time to 12-hour format, treating 0 as 12
      return `${mm}/${dd}/${yyyy} ${hh}:${min} ${period}`;
  }
};

export const getTimeDuration = (timestamp, t) => {
  const now = Date.now();
  const difference = timestamp - now;
  const absDifference = Math.abs(difference);
  const timeInMinutes = Math.floor(absDifference / 60000);

  if (difference === 0) {
    return t('time.format.before.now');
  } else if (difference > 0) {
    if (timeInMinutes < 60) return t('time.format.after.minutes', { time: timeInMinutes });
    if (timeInMinutes < 1440) return t('time.format.after.hours', { time: Math.floor(timeInMinutes / 60) });
    if (timeInMinutes < 10080) return t('time.format.after.days', { time: Math.floor(timeInMinutes / 1440) });
    if (timeInMinutes < 43200) return t('time.format.after.weeks', { time: Math.floor(timeInMinutes / 10080) });
    if (timeInMinutes < 525600) return t('time.format.after.months', { time: Math.floor(timeInMinutes / 43200) });
    return t('time.format.after.years', { time: Math.floor(timeInMinutes / 525600) });
  } else {
    if (timeInMinutes < 60) return t('time.format.before.minutes', { time: timeInMinutes });
    if (timeInMinutes < 1440) return t('time.format.before.hours', { time: Math.floor(timeInMinutes / 60) });
    if (timeInMinutes < 10080) return t('time.format.before.days', { time: Math.floor(timeInMinutes / 1440) });
    if (timeInMinutes < 43200) return t('time.format.before.weeks', { time: Math.floor(timeInMinutes / 10080) });
    if (timeInMinutes < 525600) return t('time.format.before.months', { time: Math.floor(timeInMinutes / 43200) });
    return t('time.format.before.years', { time: Math.floor(timeInMinutes / 525600) });
  }
};

export const formatOpeningStatus = (openingStatus, t) => {
  const now = Date.now();
  const { nextOpeningTime, nextClosingTime } = openingStatus;

  if (!nextOpeningTime && !nextClosingTime) {
    return '';
  }

  const MINUTES_IN_HOUR = 60;
  const MINUTES_IN_DAY = 1440;

  const formatApproxTime = (minutes) => {
    if (minutes < MINUTES_IN_HOUR) {
      // Exact minutes under 60
      return `${minutes} ${t('time.search_time.minutes')}`;
    }
    if (minutes < MINUTES_IN_DAY) {
      // Rounded hours under 24h
      const hours = Math.round(minutes / MINUTES_IN_HOUR);
      return `${t('time.search_time.approx_prefix')} ${hours} ${t('time.search_time.hours')}`;
    }
    // 24h or more → rounded days
    const days = Math.round(minutes / MINUTES_IN_DAY);
    return `${t('time.search_time.approx_prefix')} ${days} ${t('time.search_time.days')}`;
  };

  if (nextOpeningTime && nextOpeningTime > now) {
    const timeUntilOpen = Math.floor((nextOpeningTime - now) / 60000);
    return `${t('time.search_time.open_in')}${formatApproxTime(timeUntilOpen)}`;
  }

  if (nextClosingTime && nextClosingTime > now) {
    const timeUntilClose = Math.floor((nextClosingTime - now) / 60000);
    return `${t('time.search_time.prefix')}${formatApproxTime(timeUntilClose)}${t('time.search_time.postfix')}`;
  }

  return t('time.search_time.regularly');
};

export const endsWith = (str, suffix) => {
  return str.slice(-suffix.length) === suffix;
}

export const convertCentsToEuros = (cents, locale) => {
  if (typeof cents !== 'number' || isNaN(cents)) {
    return null;
  }

  // console.log(locale);
  const euros = cents / 100;
  // Format to 2 decimal places and add the Euro symbol
  return euros.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}

export const getMonthName = (monthIndex, locale) => {
  const monthDE = [
    { long: "Januar", short: "Jan" },
    { long: "Februar", short: "Feb" },
    { long: "März", short: "Mär" },
    { long: "April", short: "Apr" },
    { long: "Mai", short: "Mai" },
    { long: "Juni", short: "Jun" },
    { long: "Juli", short: "Jul" },
    { long: "August", short: "Aug" },
    { long: "September", short: "Sep" },
    { long: "Oktober", short: "Okt" },
    { long: "November", short: "Nov" },
    { long: "Dezember", short: "Dez" }
  ];

  const monthEN = [
    { long: "January", short: "Jan" },
    { long: "February", short: "Feb" },
    { long: "March", short: "Mar" },
    { long: "April", short: "Apr" },
    { long: "May", short: "May" },
    { long: "June", short: "Jun" },
    { long: "July", short: "Jul" },
    { long: "August", short: "Aug" },
    { long: "September", short: "Sep" },
    { long: "October", short: "Oct" },
    { long: "November", short: "Nov" },
    { long: "December", short: "Dec" }
  ];
  console.log('locale', locale);

  switch (locale) {
    case 'de':

      return monthDE[monthIndex].long;

    case 'en':

      return monthEN[monthIndex].long;

    default:
      return monthEN[monthIndex].long;
  }
}

export const getShortDay = (day, language) => {
  const days = {
    EN: {
      MONDAY: "Mo",
      TUESDAY: "Tu",
      WEDNESDAY: "We",
      THURSDAY: "Th",
      FRIDAY: "Fr",
      SATURDAY: "Sa",
      SUNDAY: "Su",
    },
    DE: {
      MONDAY: "Mo",
      TUESDAY: "Di",
      WEDNESDAY: "Mi",
      THURSDAY: "Do",
      FRIDAY: "Fr",
      SATURDAY: "Sa",
      SUNDAY: "So",
    },
  };
  // Convert the input day to uppercase to match the keys in the dictionary
  const uppercaseDay = day.toUpperCase();

  // Check if the language exists in the dictionary
  if (!days[language]) {
    throw new Error(`Unsupported language code: ${language}`);
  }

  // Check if the day exists in the selected language's dictionary
  const translatedDay = days[language][uppercaseDay];
  if (!translatedDay) {
    throw new Error(`Unsupported day: ${day} for language ${language}`);
  }

  return translatedDay;
}

export const copyToClipboard = (content) => {
  navigator.clipboard.writeText(content)
    .then(() => {
      alert("Copied to clipboard!");
    })
    .catch(err => {
      console.error("Failed to copy: ", err);
    });
};

export const replaceUmlauts = (str) => {
  return str.replace(/ä/g, 'ae')
            .replace(/ö/g, 'oe')
            .replace(/ü/g, 'ue')
            .replace(/Ä/g, 'Ae')
            .replace(/Ö/g, 'Oe')
            .replace(/Ü/g, 'Ue');
}

